import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ATSAuthContext = createContext(null);

export const useATSAuth = () => {
  const context = useContext(ATSAuthContext);
  if (!context) {
    throw new Error('useATSAuth must be used within ATSAuthProvider');
  }
  return context;
};

export const ATSAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('ats_token'));
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/ats/auth/login`, {
        username,
        password
      });
      const { access_token, user: userData, roles: userRoles } = response.data;
      localStorage.setItem('ats_token', access_token);
      setToken(access_token);
      setUser(userData);
      setRoles(userRoles);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const logout = useCallback(async () => {
    try {
      if (token) {
        await axios.post(`${API_URL}/api/ats/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('ats_token');
    setToken(null);
    setUser(null);
    setRoles([]);
  }, [token]);

  const fetchMe = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/ats/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      setRoles(response.data.roles || []);
    } catch (error) {
      console.error('Auth check failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const hasRole = useCallback((role) => {
    // Case-insensitive role check
    return roles.some(r => r.toLowerCase() === role.toLowerCase());
  }, [roles]);

  const isAdmin = useCallback(() => {
    return hasRole('admin') || hasRole('superuser');
  }, [hasRole]);

  const hasPIIAccess = useCallback(() => {
    return hasRole('pii') || hasRole('admin');
  }, [hasRole]);

  // Create authenticated axios instance that updates with token
  const api = React.useMemo(() => {
    const instance = axios.create({
      baseURL: API_URL,
    });
    
    // Add request interceptor to always use current token
    instance.interceptors.request.use(
      (config) => {
        const currentToken = localStorage.getItem('ats_token');
        if (currentToken) {
          config.headers.Authorization = `Bearer ${currentToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
    
    // Add response interceptor to handle 401 errors
    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('ats_token');
          setToken(null);
          setUser(null);
          setRoles([]);
        }
        return Promise.reject(error);
      }
    );
    
    return instance;
  }, []);

  const value = {
    user,
    token,
    roles,
    loading,
    login,
    logout,
    hasRole,
    isAdmin,
    hasPIIAccess,
    api
  };

  return (
    <ATSAuthContext.Provider value={value}>
      {children}
    </ATSAuthContext.Provider>
  );
};

export default ATSAuthContext;
